import React from 'react';
import { Box, Menu, Pagination, Paragraph, Table, Text, TextButton } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '@zg-rentals/zdash';
import { makeTableConfig, type MonthlyPropertyStats, TableType } from './AdSpendReport.config';
import { filterStatsForBoost, filterStatsForExtend } from './AdSpendReport.utils';
import type { CpmBoostAdPreviewsDto } from '@zg-rentals/ts-customer-analytics-client';
import { CustomerAnalyticsUserBoostPreviewWebApiHandler_getV2 } from '@zg-rentals/ts-customer-analytics-client';
import { BoostAdPreviewsModal } from '@zg-rentals/rental-revenue-components';
import { exportBoostAdPdf } from '../../../../utils/exportBoostAdPdf';
import { renderBoostPreviewOffscreen } from '../../../../utils/renderBoostPreviewOffscreen';
import { ErrorModal } from '../../../SharedComponents';
import { styled } from '@/styled-system/jsx';
import type { PackageFilter } from './AdSpendReportTable';
import { PropertyDetailModal } from './PropertyDetailModal';
import { endOfMonth, format, min, parse, startOfMonth } from 'date-fns';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitAdSpendTablePreviewAdEvent } from '../../../../analytics/events';
import { formatNumber } from '../../../../utils';
import { ZG_BOOST_AD_PDF_EXPORT } from '../../../../constants';

const stickyStyles = css.raw({
  position: 'sticky',
  right: 0,
  zIndex: 2,
  _before: {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '15px',
    pointerEvents: 'none',
    boxShadow: '-5px 0 5px rgba(0, 0, 0, 0.15)',
  },
  '&.is-stuck': {
    position: 'static',
    _before: {
      display: 'none',
    },
  },
});

const CustomHeader = styled(Table.HeaderCell, {
  base: {
    whiteSpace: 'nowrap',
  },
});

const PAGE_SIZES = [50, 100, 200];

export const AdSpendAdvancedTable = ({
  tableType,
  selectedMonthStats,
  packageFilter,
}: {
  tableType: TableType;
  monthlyStatsByMonthAndYear: Array<MonthlyPropertyStats>;
  selectedMonthStats: MonthlyPropertyStats | null;
  packageFilter?: PackageFilter | 'ALL';
}) => {
  const { user } = useRentalManagerUser();
  const [showBoostAdPreviewsModal, setShowBoostAdPreviewsModal] = useState(false);
  const [boostAdPreviews, setBoostAdPreviews] = useState<CpmBoostAdPreviewsDto | undefined>();
  const [boostAdPreviewsError, setBoostAdPreviewsError] = useState<string | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [selectedProperty, setSelectedProperty] = useState<{
    listingAlias: string;
    propertyName: string;
  } | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const { startDate, endDate } = useMemo(() => {
    if (!selectedMonthStats?.monthAndYear) {
      return { startDate: '', endDate: '' };
    }
    const monthDate = parse(selectedMonthStats.monthAndYear, 'MMMM yyyy', new Date());
    const monthEnd = endOfMonth(monthDate);
    const today = new Date();
    const effectiveEndDate = min([monthEnd, today]);
    return {
      startDate: format(startOfMonth(monthDate), 'yyyyMMdd'),
      endDate: format(effectiveEndDate, 'yyyyMMdd'),
    };
  }, [selectedMonthStats?.monthAndYear]);

  const filteredStats = useMemo(() => {
    if (!selectedMonthStats) {
      return selectedMonthStats;
    }

    let filtered = selectedMonthStats.stats;

    if (tableType === TableType.BOOST) {
      filtered = filterStatsForBoost(filtered);
    } else if (tableType === TableType.EXTEND) {
      filtered = filterStatsForExtend(filtered);
    } else if (tableType === TableType.PACKAGES) {
      filtered = filtered.filter((stat) => stat.adproductTypes.length > 0);
    }

    if ((tableType === TableType.ALL || tableType === TableType.PACKAGES) && packageFilter && packageFilter !== 'ALL') {
      filtered = filtered.filter((stat) => {
        return stat.adproductTypes.includes(packageFilter);
      });
    }

    return {
      monthAndYear: selectedMonthStats.monthAndYear,
      stats: filtered,
    };
  }, [tableType, selectedMonthStats, packageFilter]);

  const filteredStatsArray = useMemo(() => filteredStats?.stats || [], [filteredStats]);

  // Check if there are any stats with spotlight days
  const hasSpotlightDays = useMemo(() => {
    return filteredStatsArray.some((stat) => (stat.totalDaysSpotlighted ?? 0) > 0);
  }, [filteredStatsArray]);

  const paginatedStats = useMemo(() => {
    const startIndex = currentPageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredStatsArray.slice(startIndex, endIndex);
  }, [filteredStatsArray, currentPageIndex, pageSize]);

  const totalPages = Math.ceil(filteredStatsArray.length / pageSize);

  // Reset to page 0 (first page) when filters change
  useEffect(() => {
    setCurrentPageIndex(0);
  }, [filteredStatsArray.length, pageSize]);

  const tableRef = useRef<HTMLDivElement>(null);
  const [isInTheRight, setIsInTheRight] = useState(false);

  const onScrollWrapper = useMemo(
    () =>
      debounce(() => {
        const container = tableRef.current;
        if (!container) return;

        const isScrollInTheRight = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
        setIsInTheRight(isScrollInTheRight);
      }, 50),
    [],
  );

  useEffect(() => {
    const container = tableRef.current;
    if (!container) return;

    container.addEventListener('scroll', onScrollWrapper);
    window.addEventListener('resize', onScrollWrapper);
    onScrollWrapper();

    return () => {
      container.removeEventListener('scroll', onScrollWrapper);
      window.removeEventListener('resize', onScrollWrapper);
    };
  }, [onScrollWrapper]);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [tableType]);

  const previewAd = (insertionOrderId: string, isSingleFamily: boolean) => {
    if (user) emitAdSpendTablePreviewAdEvent({ user });
    setShowBoostAdPreviewsModal(true);
    CustomerAnalyticsUserBoostPreviewWebApiHandler_getV2({ insertionOrderId, isSingleFamily })
      .then((response) => {
        setBoostAdPreviews(response);
        return;
      })
      .catch(() => setBoostAdPreviewsError('Failed to preview ad.'));
  };

  const downloadAd = async (insertionOrderId: string, isSingleFamily: boolean, propertyName: string) => {
    setDownloadingIds((prev) => new Set(prev).add(insertionOrderId));
    try {
      const response = await CustomerAnalyticsUserBoostPreviewWebApiHandler_getV2({
        insertionOrderId,
        isSingleFamily,
      });
      const element = await renderBoostPreviewOffscreen(response);
      try {
        await exportBoostAdPdf(element, propertyName || `Boost_Ad_${insertionOrderId}`);
      } finally {
        document.body.removeChild(element);
      }
    } catch {
      setBoostAdPreviewsError('Failed to download ad preview.');
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(insertionOrderId);
        return next;
      });
    }
  };

  const handlePropertyClick = (listingAlias: string, propertyName: string) => {
    setSelectedProperty({ listingAlias, propertyName });
  };

  const selectedConfig = React.useMemo(
    () =>
      makeTableConfig({
        tableType,
        previewAd,
        onPropertyClick: handlePropertyClick,
        downloadAd: ZG_BOOST_AD_PDF_EXPORT ? downloadAd : undefined,
        downloadingIds,
      }),
    [tableType, downloadingIds],
  );

  return (
    <>
      <Table.Responsive ref={tableRef}>
        <Table.Root aria-label="ad spend table">
          <Table.Header>
            <Table.Row>
              {selectedConfig.map((config, idx) => (
                <React.Fragment key={config.header}>
                  {hasSpotlightDays && config.isSticky && idx === selectedConfig.findIndex((c) => c.isSticky) && (
                    <CustomHeader css={{ textAlign: 'right' }}>Spotlight days</CustomHeader>
                  )}
                  <CustomHeader
                    css={config.isSticky ? stickyStyles : undefined}
                    className={config.isSticky && isInTheRight ? 'is-stuck' : ''}
                  >
                    {config.header}
                  </CustomHeader>
                </React.Fragment>
              ))}
              {tableType === TableType.BOOST && (
                <React.Fragment key="boost-preview">
                  {hasSpotlightDays && <CustomHeader css={{ textAlign: 'right' }}>Spotlight days</CustomHeader>}
                  <CustomHeader css={stickyStyles} className={isInTheRight ? 'is-stuck' : ''}>
                    Preview
                  </CustomHeader>
                </React.Fragment>
              )}
              {hasSpotlightDays && !selectedConfig.some((c) => c.isSticky) && tableType !== TableType.BOOST && (
                <CustomHeader css={{ textAlign: 'right' }}>Spotlight days</CustomHeader>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedStats.map((stat, index) => (
              <Table.Row key={`${stat.currentListingAlias}-${index}`}>
                {selectedConfig.map((config, idx) => {
                  const value = config.field ? stat[config.field] : stat;
                  return (
                    <React.Fragment key={`${config.field}-${config.header}`}>
                      {hasSpotlightDays && config.isSticky && idx === selectedConfig.findIndex((c) => c.isSticky) && (
                        <Table.Cell css={{ textAlign: 'right' }}>
                          <Text textStyle="body-sm">{formatNumber(stat.totalDaysSpotlighted)}</Text>
                        </Table.Cell>
                      )}
                      <Table.Cell
                        css={config.isSticky ? stickyStyles : undefined}
                        className={config.isSticky && isInTheRight ? 'is-stuck' : ''}
                      >
                        {config.renderer(value, stat)}
                      </Table.Cell>
                    </React.Fragment>
                  );
                })}
                {tableType === TableType.BOOST && (
                  <React.Fragment key="boost-preview-cell">
                    {hasSpotlightDays && (
                      <Table.Cell css={{ textAlign: 'right' }}>
                        <Text textStyle="body-sm">{formatNumber(stat.totalDaysSpotlighted)}</Text>
                      </Table.Cell>
                    )}
                    <Table.Cell css={stickyStyles} className={isInTheRight ? 'is-stuck' : ''}>
                      {stat.insertionOrderId && (
                        <TextButton
                          css={{ whiteSpace: 'nowrap' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            previewAd(stat.insertionOrderId, stat.isSingleFamily);
                          }}
                        >
                          View ads
                        </TextButton>
                      )}
                    </Table.Cell>
                  </React.Fragment>
                )}
                {hasSpotlightDays && !selectedConfig.some((c) => c.isSticky) && tableType !== TableType.BOOST && (
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(stat.totalDaysSpotlighted)}</Text>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.Responsive>
      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 'md',
          flexWrap: 'wrap',
          gap: 'md',
        }}
      >
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'sm' }}>
          <Paragraph css={{ textStyle: 'body-sm' }}>Rows per page:</Paragraph>
          <Menu
            content={PAGE_SIZES.map((size) => (
              <Menu.Item key={size} onClick={() => setPageSize(size)}>
                {size}
              </Menu.Item>
            ))}
            trigger={<Menu.Button size="sm">{pageSize}</Menu.Button>}
          />
        </Box>
        {totalPages > 1 && (
          <Pagination
            currentPageIndex={currentPageIndex}
            totalPages={totalPages}
            onPageSelected={setCurrentPageIndex}
          />
        )}
      </Box>
      {showBoostAdPreviewsModal && boostAdPreviews && (
        <BoostAdPreviewsModal
          onClose={() => setShowBoostAdPreviewsModal(false)}
          boostAdPreviews={boostAdPreviews}
          onDownloadPdf={
            ZG_BOOST_AD_PDF_EXPORT
              ? (element, title) => {
                  exportBoostAdPdf(element, title);
                }
              : undefined
          }
        />
      )}
      {boostAdPreviewsError && (
        <ErrorModal onClose={() => setBoostAdPreviewsError(null)} error={boostAdPreviewsError} />
      )}
      {selectedProperty && (
        <PropertyDetailModal
          isOpen={true}
          onClose={() => setSelectedProperty(null)}
          listingAlias={selectedProperty.listingAlias}
          propertyName={selectedProperty.propertyName}
          startDate={startDate}
          endDate={endDate}
          tableType={tableType}
        />
      )}
    </>
  );
};
